import React, { useState, useRef, useEffect } from 'react';
import { Upload, Button, message, Spin, Empty, Card, Typography, Progress, Modal, Tag, Select } from 'antd';
import { InboxOutlined, CameraOutlined, SearchOutlined, ClearOutlined } from '@ant-design/icons';
import api, { ImageSearchResult, searchByImage as apiSearchByImage, getAllTags } from '../services/api';

const { Title } = Typography;

const MobileSearchPage: React.FC = () => {
  const [currentImage, setCurrentImage] = useState<File | null>(null);
  const [searchResults, setSearchResults] = useState<ImageSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [searchAttempted, setSearchAttempted] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const previewUrlRef = useRef<string>('');
  // 标签筛选（移动端）
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const setPreviewFromFile = (file: File) => {
    try {
      const url = URL.createObjectURL(file);
      if (previewUrlRef.current) {
        try { URL.revokeObjectURL(previewUrlRef.current); } catch {}
      }
      previewUrlRef.current = url;
      setPreviewUrl(url);
    } catch {}
  };
  const [uploadPercent, setUploadPercent] = useState<number>(0);
  const stopCameraStream = () => {
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach(t => t.stop());
      } catch {}
      streamRef.current = null;
    }
    if (videoRef.current) {
      try {
        (videoRef.current as any).srcObject = null;
      } catch {}
    }
  };

  // 新增：结果详情 Modal
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailItem, setDetailItem] = useState<any>(null);
  const openDetail = (item: any) => { setDetailItem(item); setDetailVisible(true); };
  const closeDetail = () => { setDetailVisible(false); setDetailItem(null); };

  // Utilities
  const compressImage = async (file: File, maxSide: number = 1600, quality: number = 0.92): Promise<File> => {
    try {
      if (!file || !file.type.startsWith('image/')) return file;
      if (file.size <= 800 * 1024) return file;
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const i = new window.Image();
        i.onload = () => { URL.revokeObjectURL(url); resolve(i); };
        i.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
        i.src = url;
      });
      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      if (!w || !h) return file;
      const maxWH = Math.max(w, h);
      const scale = maxWH > maxSide ? (maxSide / maxWH) : 1;
      const targetW = Math.max(1, Math.round(w * scale));
      const targetH = Math.max(1, Math.round(h * scale));
      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      if (!ctx) return file;
      ctx.imageSmoothingEnabled = true;
      (ctx as any).imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, targetW, targetH);
      const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
      if (!blob) return file;
      const compressed = new File([blob], file.name.endsWith('.jpg') || file.name.endsWith('.jpeg') ? file.name : `compressed-${Date.now()}.jpg`, { type: 'image/jpeg' });
      return compressed;
    } catch {
      return file;
    }
  };

  // Handlers
  const handleOpenCamera = async () => {
    try {
      const isSecure = typeof window !== 'undefined' ? (window as any).isSecureContext === true : true;
      if (!('mediaDevices' in navigator) || !isSecure) {
        throw new Error('MediaDevices not available or insecure context');
      }
      const constraints: MediaStreamConstraints = { video: { facingMode: { ideal: 'environment' } }, audio: false } as any;
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        try { await (videoRef.current as any).play?.(); } catch {}
      }
      setIsCameraOpen(true);
    } catch (error) {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      } else {
        message.error('无法打开摄像头，请改用上传');
      }
    }
  };

  const handleTakePhoto = () => {
    if (videoRef.current && streamRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const context = canvas.getContext('2d');
      if (context) {
        context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(async (blob) => {
          if (blob) {
            const file = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
            setCurrentImage(file);
            setIsCameraOpen(false);
            stopCameraStream();
          }
        }, 'image/jpeg');
      }
    }
  };

  useEffect(() => {
    if (currentImage) {
      const url = URL.createObjectURL(currentImage);
      if (previewUrlRef.current) {
        try { URL.revokeObjectURL(previewUrlRef.current); } catch {}
      }
      previewUrlRef.current = url;
      setPreviewUrl(url);
    }
    return () => {
      if (previewUrlRef.current) {
        try { URL.revokeObjectURL(previewUrlRef.current); } catch {}
        previewUrlRef.current = '';
      }
      stopCameraStream();
    };
  }, [currentImage]);

  // 前端去重（双保险）
  const dedupeResults = (arr: any[]): any[] => {
    try {
      const map = new Map<string, any>();
      for (const r of (arr || [])) {
        const id = (r && typeof r.id === 'number' && r.id > 0) ? `id:${r.id}` : '';
        const cont = r?.baidu_cont_sign || r?.cont_sign || r?.contsign || '';
        const fp = (r?.file_path || '').toLowerCase();
        const fn = (r?.filename || '').toLowerCase();
        const key = id || (cont ? `cont:${cont}` : '') || (fp ? `path:${fp}` : '') || (fn ? `fn:${fn}` : Math.random().toString());
        const prev = map.get(key);
        const sim = typeof r?.similarity === 'number' ? r.similarity : 0;
        const prevSim = typeof prev?.similarity === 'number' ? prev.similarity : -1;
        if (!prev || sim > prevSim) map.set(key, r);
      }
      return Array.from(map.values()).sort((a, b) => (Number(b?.similarity || 0) - Number(a?.similarity || 0)));
    } catch { return arr; }
  };

  const runImageSearch = async (filterTags?: string[]) => {
    if (!currentImage) {
      message.error('请先选择一张图片');
      return;
    }
    setLoading(true);
    setSearchAttempted(true);
    try {
      const fileToSend = currentImage;
      let resp = await apiSearchByImage(fileToSend, 20, 0.5, 'baidu_cloud', { strictBaidu: true, onUploadProgress: setUploadPercent });
      let results = Array.isArray(resp?.results) ? resp.results : [];
      if (!results || results.length === 0) {
        try {
          resp = await apiSearchByImage(fileToSend, 50, 0.4, 'baidu_cloud', { strictBaidu: true });
          results = Array.isArray(resp?.results) ? resp.results : [];
        } catch {}
      }
      // 不再过滤与当前上传同名/同路径的结果，保留自匹配结果以便确认
      try { /* 保留结果原样 */ } catch {}
      results = dedupeResults(results);
      if (Array.isArray(filterTags) && filterTags.length > 0) {
        results = results.filter((r: any) => Array.isArray(r?.tags) && filterTags.every(t => r.tags.includes(t)));
      }
      setSearchResults(results as ImageSearchResult[]);
      if (!Array.isArray(resp?.results)) {
        message.warning('未获取到结果列表');
      } else if ((results?.length || 0) === 0) {
        message.info('未找到相似图片，可尝试换角度拍摄或更清晰图片');
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail || '搜索失败';
      message.error(detail);
    } finally {
      setLoading(false);
      setUploadPercent(0);
    }
  };
  const handleSearch = async () => runImageSearch(selectedTags);

  const openTagPicker = async () => {
    setTagPickerOpen(true);
    if (availableTags.length === 0) {
      try {
        const res: any = await getAllTags();
        const list: string[] = Array.isArray(res) ? res : (Array.isArray(res?.tags) ? res.tags : []);
        setAvailableTags(list.filter((t) => typeof t === 'string' && t.trim()).map(t => t.trim()));
      } catch {
        message.error('获取标签列表失败');
      }
    }
  };

  const handleClear = () => {
    setCurrentImage(null);
    setSearchResults([]);
    setSearchAttempted(false);
    if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
    }
    setIsCameraOpen(false)
  };

  return (
    <div className="mobile-search-page">
      <Title level={4} style={{ textAlign: 'center', marginBottom: '24px', fontSize: '20px' }}>以图搜图</Title>
      {/* 标签选择入口 + 已选标签展示 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Button onClick={openTagPicker}>选择标签</Button>
        {selectedTags.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {selectedTags.map(t => (
              <Tag key={t} color="blue" style={{ marginBottom: 0 }}>{t}</Tag>
            ))}
          </div>
        )}
      </div>
      <input
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          if (f) {
            setCurrentImage(f);
            setPreviewFromFile(f);
            setIsCameraOpen(false);
          }
        }}
      />
      
      {!currentImage && !isCameraOpen && (
        <div className="upload-options">
          <Upload.Dragger
            name="file"
            multiple={false}
            showUploadList={false}
            beforeUpload={file => {
              setCurrentImage(file);
              setPreviewFromFile(file);
              return false;
            }}
            accept="image/jpeg,image/png"
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">点击或拖拽图片到此区域</p>
          </Upload.Dragger>
          {/* 按需去掉摄像头拍照入口 */}
        </div>
      )}

      {isCameraOpen && (
        <div className="camera-container">
          <video ref={videoRef} autoPlay playsInline muted className="webcam-video" />
          <div className="camera-controls">
            <Button type="primary" onClick={handleTakePhoto} block>拍摄照片</Button>
            <Button onClick={handleClear} block>取消</Button>
          </div>
        </div>
      )}

      {currentImage && !isCameraOpen && (
        <div className="image-preview-container">
          <img src={previewUrl} alt="当前图片" className="preview-image" decoding="async" loading="eager" style={{ maxWidth: 360, width: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
          <div className="image-actions">
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} block>
              开始搜索
            </Button>
            <Button icon={<ClearOutlined />} onClick={handleClear} block>
              清除图片
            </Button>
          </div>
        </div>
      )}

      {loading && (
        <div className="loading-spinner" style={{ padding: 12 }}>
          {uploadPercent > 0 && uploadPercent < 100 ? (
            <div>
              <div style={{ marginBottom: 8, color: '#666' }}>上传中 {uploadPercent}%</div>
              <Progress percent={uploadPercent} size="small" showInfo={false} />
            </div>
          ) : (
            <Spin tip="正在搜索..." />
          )}
        </div>
      )}

      {searchResults.length > 0 && (
        <div className="search-results">
          <Title level={5}>搜索结果</Title>
          <div className="results-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 8
          }}>
            {searchResults.map((result) => {
              const anyRes: any = result as any;
              const fp = typeof anyRes.file_path === 'string' ? anyRes.file_path : '';
              const isRemote = fp.startsWith('http');
              const isLocalUploaded = fp.startsWith('/uploads/');
              const hasValidId = typeof anyRes.id === 'number' && anyRes.id > 0;
              const safeFilename = typeof (result as any).filename === 'string' ? (result as any).filename : '';
              const hasFilename = !!safeFilename && safeFilename !== 'baidu_item';
                  // 为避免缩略图与详情不一致，优先使用与详情一致的 /uploads/{filename}
                  // 若无 filename 再使用缩略图或远程 URL
                  const cacheBust = anyRes.baidu_cont_sign ? `?v=${encodeURIComponent(String(anyRes.baidu_cont_sign))}` : (hasValidId ? `?v=${anyRes.id}` : '');
                  const displaySrc = hasFilename
                    ? `/uploads/${safeFilename}${cacheBust}`
                    : (hasValidId
                        ? `/api/images/${anyRes.id}/thumbnail?size=300${cacheBust}`
                        : (isRemote ? fp : (isLocalUploaded ? fp : '')));
                  const fallbackSrc = hasValidId
                    ? `/api/images/${anyRes.id}/thumbnail?size=300${cacheBust}`
                    : (isRemote ? fp : (hasFilename ? `/uploads/${safeFilename}${cacheBust}` : (isLocalUploaded ? fp : '')));
              const previewSrc = isRemote ? fp : (hasFilename ? `/uploads/${safeFilename}` : displaySrc);
              const cardKey = anyRes.baidu_cont_sign || anyRes.id || safeFilename || fp || Math.random();
              return (
              <Card
                key={cardKey}
                bodyStyle={{ padding: 0, position: 'relative' }}
                cover={
                  <div className="result-cover" onClick={() => openDetail(anyRes)} style={{
                    width: '100%',
                    aspectRatio: '1',
                    background: '#f7f7f7',
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 6
                  }}>
                    <img
                      src={displaySrc}
                      alt={hasFilename ? safeFilename : ''}
                      className="result-image"
                      loading="lazy"
                      decoding="async"
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        background: '#fff'
                      }}
                      onError={(e) => {
                        try {
                          const img = e.currentTarget as HTMLImageElement;
                          if (img.getAttribute('data-fallback') === '1') return;
                          if (fallbackSrc && img.src !== fallbackSrc) {
                            img.setAttribute('data-fallback', '1');
                            img.src = fallbackSrc;
                          }
                        } catch {}
                      }}
                    />
                  </div>
                }
              >
                <div className="similarity-badge">
                  {typeof (result as any).similarity === 'number' ? (((result as any).similarity * 100).toFixed(1) + '%') : ''}
                </div>
                <div className="result-meta" style={{ padding: '8px' }}>
                  <div className="result-line" style={{ fontSize: 12, color: '#555' }}>
                    相似度：{typeof (result as any).similarity === 'number' ? `${((result as any).similarity * 100).toFixed(1)}%` : '—'}
                  </div>
                  {hasFilename && (
                    <div className="result-line" style={{ fontSize: 12, color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      文件名：{safeFilename}
                    </div>
                  )}
                  {result.description && (
                    <div className="result-line" style={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      描述：{result.description}
                    </div>
                  )}
                  {Array.isArray(result.tags) && result.tags.length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      <div className="result-line" style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>标签：</div>
                      <div className="result-tags" style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {result.tags.slice(0, 3).map((tag, idx) => (
                          <span key={idx} style={{ background: '#f0f0f0', borderRadius: 4, padding: '0 6px', fontSize: 11, color: '#555' }}>{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
              );
            })}
          </div>
        </div>
      )}

      {!loading && searchResults.length === 0 && searchAttempted && (
        <div className="empty-results">
          <Empty description="未找到相似图片" />
        </div>
      )}

      <Modal open={detailVisible} onCancel={closeDetail} footer={null} title={detailItem?.filename || '图片详情'} width="90vw">
        {detailItem && (
          <div>
            <img src={(detailItem?.file_path?.startsWith('http') ? detailItem.file_path : (detailItem?.filename ? `/uploads/${detailItem.filename}` : ''))} alt={detailItem?.filename || ''} style={{ width: '100%', marginBottom: 12, objectFit: 'contain', maxHeight: '70vh', background: '#fff' }} onError={(e) => {
              try {
                const img = e.currentTarget as HTMLImageElement;
                const fp = typeof detailItem?.file_path === 'string' ? detailItem.file_path : '';
                const remote = fp.startsWith('http');
                const fallback = remote ? fp : (detailItem?.filename ? `/uploads/${detailItem.filename}` : '');
                if (fallback && img.src !== fallback) {
                  img.src = fallback;
                }
              } catch {}
            }} />
            {detailItem?.description && (
              <div style={{ marginBottom: 8 }}><strong>描述：</strong>{detailItem.description}</div>
            )}
            <div style={{ marginBottom: 8 }}><strong>标签：</strong>
              <div style={{ marginTop: 6 }}>
                {Array.isArray(detailItem?.tags) && detailItem.tags.length > 0 ? (
                  detailItem.tags.map((t: string, i: number) => <Tag key={i} style={{ marginBottom: 6 }}>{t}</Tag>)
                ) : (
                  <span style={{ color: '#999' }}>无标签</span>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* 标签选择弹窗：多选 + 立即搜索 */}
      <Modal
        open={tagPickerOpen}
        title="选择标签"
        onCancel={() => setTagPickerOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setTagPickerOpen(false)}>取消</Button>,
          <Button key="search" type="primary" onClick={() => { setTagPickerOpen(false); runImageSearch(selectedTags); }}>立即搜索</Button>,
        ]}
      >
        <div style={{ marginBottom: 8 }}>可多选标签用于筛选搜索结果（需先选择图片）</div>
        <Select
          mode="multiple"
          allowClear
          placeholder="请选择标签"
          options={availableTags.map(t => ({ value: t, label: t }))}
          value={selectedTags}
          onChange={(vals) => setSelectedTags(vals as string[])}
          style={{ width: '100%' }}
        />
      </Modal>
    </div>
  );
};

export default MobileSearchPage;
